import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Stack,
  InputAdornment,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  TablePagination,
  Badge,
} from '@mui/material';
import {
  Search,
  Visibility,
  LocalShipping,
  CheckCircle,
  Inventory,
  LocalShippingOutlined,
  ViewList,
  ViewModule,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Print,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';

interface OrderItem {
  id: string;
  order_id: string;
  product_id: number;
  product_name: string;
  product_price: number;
  quantity: number;
  total_price: number;
  created_at: string;
  size: string;
  color_name: string;
  item_delivery_status?: string;
  shipping_company?: string;
  tracking_number?: string;
  product?: Product;
  // 취소/교환/환불 정보
  cancel_exchange_request?: {
    id: string;
    type: 'cancel' | 'exchange' | 'refund';
    status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed';
    reason: string;
    created_at: string;
  };
}

interface Product {
  product_id: number;
  product_name: string;
  price: number;
  main_images: string[];
  seller_id: string;
}

interface Order {
  id: string;
  order_number: string;
  order_date: string;
  payment_method: string;
  payment_status: string;
  customer_name: string;
  customer_phone: string;
  recipient_name: string;
  recipient_phone: string;
  shipping_address: string;
  special_requests: string;
  total_amount: number;
  status: 'preparing' | 'confirmed' | 'shipping' | 'delivered' | 'cancelled';
  order_items?: OrderItem[];
}

const statusLabel = (status: string, t: (key: string) => string, cancelExchangeRequest?: OrderItem['cancel_exchange_request']) => {
  // 취소/교환/환불 요청이 있는 경우
  if (cancelExchangeRequest) {
    const { type, status: reqStatus } = cancelExchangeRequest;

    // 교환 승인 후에는 실제 배송 상태를 표시 (재발송 처리를 위해)
    if (type === 'exchange' && reqStatus === 'approved') {
      // 아래 기존 상태 표시 로직으로 넘어감
    } else {
      // 그 외 취소/교환/환불 요청 상태 표시
      let label = '';
      let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'warning';

      switch (type) {
        case 'cancel':
          label = 'Cancel';
          color = 'error';
          break;
        case 'exchange':
          label = 'Exchange';
          color = 'warning';
          break;
        case 'refund':
          label = 'Refund';
          color = 'info';
          break;
      }

      switch (reqStatus) {
        case 'pending':
          label += ' Request';
          break;
        case 'approved':
          label += ' Approved';
          color = 'default'; // 회색
          break;
        case 'processing':
          label += ' Processing';
          color = 'primary';
          break;
        case 'completed':
          label += ' Completed';
          color = 'success';
          break;
        case 'rejected':
          label += ' Rejected';
          color = 'default';
          break;
      }

      return <Chip label={label} color={color} size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    }
  }

  // 기존 상태 표시
  switch (status) {
    case 'preparing':
      return <Chip label={t('sellerOrders.preparing')} color="warning" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'confirmed':
      return <Chip label={t('orders.packing')} color="success" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'shipping':
      return <Chip label={t('orders.shipping')} color="primary" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'delivered':
      return <Chip label={t('orders.delivered')} color="success" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
    case 'cancelled':
      return <Chip label={t('orders.cancelled')} color="default" size="small" sx={{ fontWeight: 700, borderRadius: 1, bgcolor: '#e0e0e0' }} />;
    default:
      return status;
  }
};

export default function SellerOrders() {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'product' | 'order'>('product');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    preparing: 0,
    confirmed: 0,
    shipping: 0,
    delivered: 0,
    cancelled: 0,
    cancel_exchange: 0,
  });

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 배송 관련 상태
  const [shippingCompany, setShippingCompany] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [customShippingCompany, setCustomShippingCompany] = useState('');
  const [showShippingDialog, setShowShippingDialog] = useState(false);
  const [orderToShip, setOrderToShip] = useState<Order | null>(null);

  const statusTabs = [
    { label: t('common.all') || 'All', value: 'all' },
    { label: t('orders.preparing'), value: 'preparing' },
    { label: t('orders.packing'), value: 'confirmed' },
    { label: t('orders.shipping'), value: 'shipping' },
    { label: t('orders.delivered'), value: 'delivered' },
    { label: t('orders.cancelled'), value: 'cancelled' },
    { label: t('sellerOrders.cancelExchangeRefund'), value: 'cancel_exchange' },
  ];

  const statusOptions = [
    { value: 'preparing', label: t('orders.preparing') },
    { value: 'confirmed', label: t('orders.packing') },
    { value: 'shipping', label: t('orders.shipping') },
    { value: 'cancelled', label: t('orders.cancelled') },
  ];

  // 배송사 목록
  const shippingCompanies = [
    '로젠택배',
    '한진택배',
    'CJ대한통운',
    '우체국택배',
    'GS편의점택배',
    'CU편의점택배',
    '롯데택배',
    t('sellerOrders.courierNameLabel') === 'sellerOrders.courierNameLabel' ? '직접입력' : '직접입력',
  ];

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (phone: string) => {
    if (!phone || phone === t('sellerOrders.noContactInfo') || phone === t('sellerOrders.noRecipientContact')) {
      return phone;
    }

    // 이미 하이픈이 포함된 경우 그대로 반환
    if (phone.includes('-')) {
      return phone;
    }

    // 숫자만 추출
    const numbers = phone.replace(/\D/g, '');

    // 11자리 숫자인 경우 000-0000-0000 형식으로 변환
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }

    // 10자리 숫자인 경우 00-0000-0000 형식으로 변환
    if (numbers.length === 10) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    }

    // 그 외의 경우 원본 반환
    return phone;
  };

  // 뷰 모드 토글 함수
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'product' ? 'order' : 'product');
  };

  // 주문 확장/축소 토글 함수
  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // 탭별 아이템 개수 계산 함수
  const calculateTabCounts = (orders: Order[]) => {
    const counts = {
      all: 0,
      preparing: 0,
      confirmed: 0,
      shipping: 0,
      delivered: 0,
      cancelled: 0,
      cancel_exchange: 0,
    };

    orders.forEach(order => {
      order.order_items?.forEach(item => {
        counts.all++;

        // 배송 상태별 카운트
        if (item.item_delivery_status === 'preparing') counts.preparing++;
        if (item.item_delivery_status === 'confirmed') counts.confirmed++;
        if (item.item_delivery_status === 'shipping') counts.shipping++;
        if (item.item_delivery_status === 'delivered') counts.delivered++;
        if (item.item_delivery_status === 'cancelled') counts.cancelled++;

        // 취소/교환/환불 요청 카운트
        if (item.cancel_exchange_request) counts.cancel_exchange++;
      });
    });

    return counts;
  };

  // 주문 데이터 가져오기
  const fetchOrders = async () => {
    if (!user) {
      console.log('사용자 정보가 없습니다.');
      return;
    }

    console.log('현재 사용자 ID:', user.id);

    try {
      setLoading(true);

      // 1. 현재 로그인한 user_id로 seller_profile 테이블에서 seller_profile.id 조회
      console.log('1단계: seller_profile 조회 시작 (user_id:', user.id, ')');
      const { data: sellerProfile, error: sellerProfileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerProfileError || !sellerProfile) {
        console.error('seller_profile 조회 실패:', sellerProfileError);
        return;
      }

      console.log('seller_profile 조회 결과:', sellerProfile);
      console.log('seller_profile.id:', sellerProfile.id);

      // 2. products 테이블에서 seller_id가 seller_profile.id와 일치하는 상품들 조회 (먼저!)
      console.log('2단계: products 조회 시작 (seller_id:', sellerProfile.id, ')');
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('seller_id', sellerProfile.id);

      if (productsError) {
        console.error('products 조회 실패:', productsError);
        return;
      }

      console.log('products 조회 결과:', products);
      console.log('products 개수:', products?.length || 0);

      // products의 id 확인
      if (products && products.length > 0) {
        console.log('products의 product_id 목록:', products.map(p => p.product_id));
        console.log('첫 번째 상품의 모든 컬럼:', products[0]);
        console.log('첫 번째 상품의 키 목록:', Object.keys(products[0]));
      }

      // 3. 현재 판매자의 상품 ID 목록 추출
      const sellerProductIds = products.map(p => p.product_id);
      console.log('3단계: 판매자 상품 ID 목록:', sellerProductIds);
      console.log('3단계: 판매자 상품 ID 타입 확인:', sellerProductIds.map(id => typeof id));

      // 상품이 없는 경우 빈 배열 반환
      if (sellerProductIds.length === 0) {
        console.log('판매자의 상품이 없습니다.');
        setOrders([]);
        setLoading(false);
        return;
      }

      // 4. order_items 조건부 조회 (RLS 무한 재귀 방지!)
      console.log('4단계: order_items 조건부 조회 시작');
      const { data: orderItems, error: orderItemsError } = await supabase
        .from('order_items')
        .select('*, color_name, size')
        .in('product_id', sellerProductIds) // 데이터베이스 레벨에서 필터링
        .order('created_at', { ascending: false }); // 최신순 정렬

      if (orderItemsError) {
        console.error('order_items 조회 실패:', orderItemsError);
        return;
      }

      console.log('order_items 조건부 조회 결과:', orderItems);
      console.log('order_items 개수:', orderItems?.length || 0);

      // 4.5. 취소/교환/환불 요청 정보 가져오기
      console.log('4.5단계: 취소/교환/환불 요청 조회 시작');
      const orderItemIds = orderItems?.map(item => item.id) || [];
      let cancelExchangeRequests: any[] = [];

      if (orderItemIds.length > 0) {
        const { data: cancelExchangeData, error: cancelExchangeError } = await supabase
          .from('cancel_exchange_requests')
          .select('id, order_item_id, type, status, reason, created_at')
          .in('order_item_id', orderItemIds);

        if (cancelExchangeError) {
          console.error('취소/교환 요청 조회 실패:', cancelExchangeError);
        } else {
          cancelExchangeRequests = cancelExchangeData || [];
          console.log('취소/교환 요청 조회 결과:', cancelExchangeRequests);
        }
      }

      // 이미 필터링된 결과이므로 추가 필터링 불필요
      const sellerOrderItems = orderItems || [];
      console.log('판매자 주문 아이템 개수:', sellerOrderItems.length);

      // 5. 주문 아이템들의 order_id로 주문 정보 조회
      const orderIds = Array.from(new Set(sellerOrderItems.map(item => item.order_id)));
      console.log('5단계: 주문 ID 목록:', orderIds);
      console.log('주문 ID 개수:', orderIds.length);

      if (orderIds.length === 0) {
        console.log('주문 ID가 없습니다. 주문이 없거나 상품이 없습니다.');
        setOrders([]);
        setLoading(false);
        return;
      }

      // 6. order_id별로 실제 주문 정보와 함께 구성
      console.log('6단계: order_id별로 실제 주문 정보 구성');
      console.log('6단계: 유니크 주문 ID 목록:', orderIds);

      // orders 테이블에서 주문 정보 가져오기
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          shipping_request,
          user_id,
          shipping_address_id,
          created_at,
          delivery_status,
          customer_email,
          guest_recipient_name,
          guest_address,
          guest_detail_address,
          guest_postal_code,
          guest_phone
        `)
        .in('id', orderIds);

      if (ordersError) {
        console.error('orders 테이블 조회 실패:', ordersError);
      }

      console.log('6단계: orders 데이터:', ordersData);

      // 사용자 정보 가져오기 (구매자)
      const userIds = ordersData?.map(order => order.user_id).filter(Boolean) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, phone, user_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('profiles 테이블 조회 실패:', profilesError);
      }

      console.log('6단계: profiles 데이터:', profilesData);

      // 배송 주소 정보 가져오기
      const addressIds = ordersData?.map(order => order.shipping_address_id).filter(Boolean) || [];
      const { data: addressesData, error: addressesError } = await supabase
        .from('user_addresses')
        .select('id, full_name, phone_number, address_line1, address_line2, postal_code')
        .in('id', addressIds);

      if (addressesError) {
        console.error('user_addresses 테이블 조회 실패:', addressesError);
      }

      console.log('6단계: addresses 데이터:', addressesData);

      const ordersMap = new Map();

      // order_id별로 그룹화하여 실제 데이터로 구성
      sellerOrderItems.forEach(item => {
        if (!ordersMap.has(item.order_id)) {
          const orderData = ordersData?.find(o => o.id === item.order_id);
          const profileData = profilesData?.find(p => p.id === orderData?.user_id);
          const addressData = addressesData?.find(a => a.id === orderData?.shipping_address_id);

          // 비회원 여부 판단 (user_id 기준 아닌 shipping_address_id 기준 - 연동 후에도 guest 주소 필드 사용)
          const isGuest = !orderData?.shipping_address_id;

          // 주소 조합
          let fullAddress = '주소 정보 없음';
          if (addressData) {
            const addressParts = [
              addressData.address_line1,
              addressData.address_line2,
              addressData.postal_code
            ].filter(Boolean);
            fullAddress = addressParts.join('\n');
          } else if (isGuest && orderData?.guest_address) {
            const guestParts = [
              orderData.guest_address,
              orderData.guest_detail_address,
              orderData.guest_postal_code
            ].filter(Boolean);
            fullAddress = guestParts.join('\n');
          }

          ordersMap.set(item.order_id, {
            id: item.order_id,
            order_number: item.order_id.slice(0, 8),
            order_date: item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            payment_method: '신용카드', // 여전히 하드코딩 (orders 테이블에 없는 경우)
            payment_status: '결제완료', // 여전히 하드코딩 (orders 테이블에 없는 경우)
            customer_name: profileData?.user_name || (isGuest ? `비회원 (${orderData?.customer_email || ''})` : `고객 ${item.order_id.slice(0, 8)}`),
            customer_phone: formatPhoneNumber(profileData?.phone || (isGuest ? orderData?.guest_phone || t('sellerOrders.noContactInfo') : t('sellerOrders.noContactInfo'))),
            recipient_name: addressData?.full_name || (isGuest ? orderData?.guest_recipient_name || `수령인 ${item.order_id.slice(0, 8)}` : `수령인 ${item.order_id.slice(0, 8)}`),
            recipient_phone: formatPhoneNumber(addressData?.phone_number || (isGuest ? orderData?.guest_phone || t('sellerOrders.noRecipientContact') : t('sellerOrders.noRecipientContact'))),
            shipping_address: fullAddress,
            special_requests: orderData?.shipping_request || '',
            total_amount: 0,
            status: item.item_delivery_status || 'preparing',
            order_items: []
          });

          // 디버깅을 위해 실제 delivery_status 값 확인
          console.log(`🔍 [ORDERS DEBUG] 주문 ${item.order_id}:`);
          console.log(`  - item.item_delivery_status: "${item.item_delivery_status}"`);
          console.log(`  - orderData?.delivery_status: "${orderData?.delivery_status}"`);
          console.log(`  - 최종 status: "${item.item_delivery_status || 'preparing'}"`);
          console.log(`  - item 전체:`, item);
        }

        const order = ordersMap.get(item.order_id);
        const product = products.find(p => p.product_id === item.product_id);
        // 해당 order_item에 대한 취소/교환/환불 요청 찾기
        const cancelExchangeRequest = cancelExchangeRequests.find(req => req.order_item_id === item.id);

        const orderItemWithProduct = {
          ...item,
          product,
          price: item.product_price,
          quantity: item.quantity,
          size: item.size || '',
          color_name: item.color_name || '',
          cancel_exchange_request: cancelExchangeRequest
        };
        order.order_items.push(orderItemWithProduct);
        order.total_amount += item.total_price || (item.product_price * item.quantity);
      });

      const ordersWithItems = Array.from(ordersMap.values());

      // 각 주문의 대표 상태를 재계산 (취소되지 않은 아이템 기준)
      ordersWithItems.forEach(order => {
        // 취소되지 않은 아이템들의 상태 수집
        const activeItems = order.order_items?.filter((item: OrderItem) =>
          item.item_delivery_status !== 'cancelled' &&
          item.item_delivery_status !== 'cancelling'
        ) || [];

        if (activeItems.length === 0) {
          // 모든 아이템이 취소된 경우
          order.status = 'cancelled';
        } else {
          // 활성 아이템 중 가장 진행된 상태를 주문 상태로 사용
          // 우선순위: delivered > shipping > confirmed > preparing
          const statuses = activeItems.map((item: OrderItem) => item.item_delivery_status);
          if (statuses.includes('delivered')) {
            order.status = 'delivered';
          } else if (statuses.includes('shipping')) {
            order.status = 'shipping';
          } else if (statuses.includes('confirmed')) {
            order.status = 'confirmed';
          } else {
            order.status = 'preparing';
          }
        }
      });

      console.log('구성된 주문 데이터:', ordersWithItems);

      // 실제 사용되는 상태값 확인
      const usedStatuses = new Set(ordersWithItems.map(order => order.status));
      console.log('실제 사용되는 상태값들:', Array.from(usedStatuses));

      setOrders(ordersWithItems);

      // 탭 카운트 업데이트
      const counts = calculateTabCounts(ordersWithItems);
      setTabCounts(counts);
    } catch (error) {
      console.error('주문 데이터 가져오기 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleUpdateStatus = async (orderId: string, newStatus: Order['status'], shippingCompany?: string, trackingNumber?: string) => {
    try {
      console.log(`🔄 상태 업데이트 시작: 주문 ${orderId} -> ${newStatus}`);

      // 현재 시간
      const now = new Date().toISOString();

      // order_items 업데이트 객체 준비
      const updateData: any = {
        item_delivery_status: newStatus  // 새로운 필드 사용
      };

      switch (newStatus) {
        case 'confirmed':
          updateData.confirmed_at = now;
          break;
        case 'shipping':
          updateData.prepared_at = now;
          updateData.shipped_at = now;
          if (shippingCompany) updateData.shipping_company = shippingCompany;
          if (trackingNumber) updateData.tracking_number = trackingNumber;
          break;
      }

      // 취소/교환/환불 요청이 없는 order_items만 업데이트 (교환 승인 제외)
      const targetOrder = orders.find(order => order.id === orderId);
      const normalItemIds = targetOrder?.order_items
        ?.filter(item =>
          !item.cancel_exchange_request ||
          (item.cancel_exchange_request.type === 'exchange' && item.cancel_exchange_request.status === 'approved')
        )
        .map(item => item.id) || [];

      if (normalItemIds.length === 0) {
        alert(t('sellerOrders.noProcessableItems'));
        return;
      }

      console.log(`📦 처리 대상 아이템 개수: ${normalItemIds.length}개`);

      // 취소/교환/환불 신청이 없는 아이템만 상태 업데이트
      const { error: itemsError } = await supabase
        .from('order_items')
        .update(updateData)
        .in('id', normalItemIds);

      if (itemsError) {
        console.error('❌ order_items 업데이트 실패:', itemsError);
        return;
      }

      console.log('✅ order_items 업데이트 성공');

      // 로컬 상태 업데이트
      setOrders(orders.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      console.log(`✅ 주문 ${orderId} 상태를 ${newStatus}로 업데이트했습니다.`);

      // 데이터 새로고침
      await fetchOrders();

    } catch (error) {
      console.error('💥 주문 상태 업데이트 실패:', error);
    }
  };


  const handleShipOrder = (order: Order) => {
    // 취소/교환/환불 요청이 없는 아이템 개수 확인 (교환 승인 제외)
    const normalItems = order.order_items?.filter(item =>
      !item.cancel_exchange_request ||
      (item.cancel_exchange_request.type === 'exchange' && item.cancel_exchange_request.status === 'approved')
    ) || [];

    if (normalItems.length === 0) {
      alert(t('sellerOrders.noShippableItems'));
      return;
    }

    // 일부 상품만 취소 신청된 경우 알림
    if (normalItems.length < (order.order_items?.length || 0)) {
      const canceledCount = (order.order_items?.length || 0) - normalItems.length;
      if (!window.confirm(t('sellerOrders.partialShipConfirm', { canceledCount, normalCount: normalItems.length }))) {
        return;
      }
    }

    setOrderToShip(order);
    setShippingCompany('');
    setTrackingNumber('');
    setCustomShippingCompany('');
    setShowShippingDialog(true);
  };

  const handleConfirmShipping = async () => {
    if (!orderToShip || !trackingNumber.trim()) return;

    const finalShippingCompany = shippingCompany === t('sellerOrders.manualInput') ? customShippingCompany : shippingCompany;
    if (!finalShippingCompany.trim()) return;

    await handleUpdateStatus(orderToShip.id, 'shipping', finalShippingCompany, trackingNumber);
    setShowShippingDialog(false);
    setOrderToShip(null);
  };

  // 프린트 기능
  const handlePrintOrder = (order: Order) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>주문서 - ${order.order_number}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              line-height: 1.6;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-weight: bold;
              font-size: 18px;
              margin-bottom: 10px;
              color: #333;
              border-left: 4px solid #007bff;
              padding-left: 10px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
            }
            .info-item {
              margin-bottom: 8px;
            }
            .info-label {
              font-weight: bold;
              color: #555;
            }
            .info-value {
              margin-left: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            .total {
              text-align: right;
              font-weight: bold;
              font-size: 18px;
              margin-top: 15px;
              padding: 10px;
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>주 문 서</h1>
            <h2>주문번호: ${order.order_number}</h2>
          </div>

          <div class="section">
            <div class="section-title">주문 정보</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">주문번호:</span>
                <span class="info-value">${order.order_number}</span>
              </div>
              <div class="info-item">
                <span class="info-label">주문일시:</span>
                <span class="info-value">${order.order_date}</span>
              </div>
              <div class="info-item">
                <span class="info-label">결제수단:</span>
                <span class="info-value">${order.payment_method}</span>
              </div>
              <div class="info-item">
                <span class="info-label">결제상태:</span>
                <span class="info-value">${order.payment_status}</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">구매자 정보</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">이름:</span>
                <span class="info-value">${order.customer_name}</span>
              </div>
              <div class="info-item">
                <span class="info-label">연락처:</span>
                <span class="info-value">${order.customer_phone}</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">배송지 정보</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">수령인:</span>
                <span class="info-value">${order.recipient_name}</span>
              </div>
              <div class="info-item">
                <span class="info-label">수령인 연락처:</span>
                <span class="info-value">${order.recipient_phone}</span>
              </div>
              <div class="info-item" style="grid-column: 1 / -1;">
                <span class="info-label">배송주소:</span>
                <span class="info-value" style="white-space: pre-line;">${order.shipping_address}</span>
              </div>
              <div class="info-item" style="grid-column: 1 / -1;">
                <span class="info-label">요청사항:</span>
                <span class="info-value">${order.special_requests || '없음'}</span>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">주문 상품</div>
            <table>
              <thead>
                <tr>
                  <th>상품명</th>
                  <th>옵션</th>
                  <th>수량</th>
                  <th>단가</th>
                  <th>소계</th>
                </tr>
              </thead>
              <tbody>
                ${order.order_items?.map(item => `
                  <tr>
                    <td>${item.product?.product_name || item.product_name}</td>
                    <td>${item.color_name && item.size ? `${item.color_name} / ${item.size}` : (item.color_name || item.size || '-')}</td>
                    <td>${item.quantity}</td>
                    <td>${item.product_price.toLocaleString()}원</td>
                    <td>${item.total_price.toLocaleString()}원</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div class="total">
              총 결제금액: ${order.total_amount.toLocaleString()}원
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // 필터링 로직: 상품별 보기일 때는 개별 아이템 필터링, 주문별 보기일 때는 주문 단위 필터링
  const filteredOrders = orders.map(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.order_items?.some(item =>
                           item.product?.product_name.toLowerCase().includes(searchTerm.toLowerCase())
                         );

    if (!matchesSearch) return null;

    // 상품별 보기: 개별 아이템 필터링
    if (viewMode === 'product') {
      const filteredItems = order.order_items?.filter(item => {
        if (tab === 0) return true; // All
        if (tab === 1) return item.item_delivery_status === 'preparing';
        if (tab === 2) return item.item_delivery_status === 'confirmed';
        if (tab === 3) return item.item_delivery_status === 'shipping';
        if (tab === 4) return item.item_delivery_status === 'delivered';
        if (tab === 5) return item.item_delivery_status === 'cancelled';
        if (tab === 6) return !!item.cancel_exchange_request;
        return false;
      });

      if (!filteredItems || filteredItems.length === 0) return null;

      return {
        ...order,
        order_items: filteredItems
      };
    }

    // 주문별 보기: 주문 단위 필터링 (기존 로직)
    const matchesStatus =
      tab === 0 ||
      (tab === 1 && order.order_items?.some(item => item.item_delivery_status === 'preparing')) ||
      (tab === 2 && order.order_items?.some(item => item.item_delivery_status === 'confirmed')) ||
      (tab === 3 && order.order_items?.some(item => item.item_delivery_status === 'shipping')) ||
      (tab === 4 && order.order_items?.some(item => item.item_delivery_status === 'delivered')) ||
      (tab === 5 && order.order_items?.some(item => item.item_delivery_status === 'cancelled')) ||
      (tab === 6 && order.order_items?.some(item => item.cancel_exchange_request));

    return matchesStatus ? order : null;
  }).filter((order): order is Order => order !== null);

  // 페이지네이션 핸들러
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // 페이지를 0으로 리셋
  };

  // 페이지네이션 적용된 주문 목록
  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight={700} mb={3} sx={styles.title}>
        {t('sellerOrders.title')}
      </Typography>
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            placeholder={t('sellerOrders.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: styles.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...styles.input, width: 240 }}
            size="small"
          />
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              ml: { sm: 2 },
              '& .MuiTab-root': {
                color: styles.text.secondary,
                '&.Mui-selected': {
                  color: styles.text.primary
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: styles.text.primary
              },
              '& .MuiTabs-scrollButtons': {
                color: styles.text.primary,
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }
            }}
            TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
          >
            {statusTabs.map((t) => {
              // 접수 대기와 포장중만 배지 표시
              const showBadge = t.value === 'preparing' || t.value === 'confirmed';
              const count = tabCounts[t.value as keyof typeof tabCounts];
              const badgeContent = count > 99 ? '99+' : count;

              return (
                <Tab
                  key={t.value}
                  label={
                    showBadge ? (
                      <Badge
                        badgeContent={badgeContent}
                        color="primary"
                        sx={{
                          '& .MuiBadge-badge': {
                            right: -12,
                            top: 2,
                            fontSize: '0.7rem',
                            minWidth: '18px',
                            height: '18px',
                            fontWeight: 700,
                          }
                        }}
                      >
                        {t.label}
                      </Badge>
                    ) : (
                      t.label
                    )
                  }
                  sx={{ fontWeight: 700, minHeight: 40 }}
                />
              );
            })}
          </Tabs>
          <Box flex={1} />
          {/* 뷰 모드 토글 버튼 */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newViewMode) => {
              if (newViewMode !== null) {
                setViewMode(newViewMode);
              }
            }}
            sx={{
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 100,
                border: `1px solid ${styles.table.cell.borderColor}`,
                color: styles.text.primary,
                '&.Mui-selected': {
                  backgroundColor: styles.button.primary.bgcolor,
                  color: styles.button.primary.color,
                  '&:hover': {
                    backgroundColor: styles.button.primary['&:hover'].bgcolor,
                  }
                }
              }
            }}
          >
            <ToggleButton value="product">
              <ViewList sx={{ mr: 1 }} />
              {t('sellerOrders.byProduct')}
            </ToggleButton>
            <ToggleButton value="order">
              <ViewModule sx={{ mr: 1 }} />
              {t('sellerOrders.byOrder')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>
      <TableContainer component={Paper} sx={styles.paper}>
        <Table>
          <TableHead>
            <TableRow sx={styles.table.header}>
              {viewMode === 'product' ? (
                <>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.orderNumber')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.orderDate')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.productName')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.option')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.count')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.state')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.action')}</TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.orderNumber')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.orderDate')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.buyer')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.itemCount')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.totalAmount')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.state')}</TableCell>
                  <TableCell sx={styles.table.cell}>{t('sellerOrders.action')}</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={styles.table.cell}>
                  <Typography color={styles.text.secondary}>{t('sellerOrders.noOrders')}</Typography>
                </TableCell>
              </TableRow>
            ) : viewMode === 'product' ? (
              // 상품별 보기: 각 주문 아이템을 개별 행으로 표시
              paginatedOrders.map((order) =>
                order.order_items?.map((item, index) => (
                  <TableRow key={`${order.id}-${index}`}>
                    <TableCell sx={{ ...styles.table.cell, fontWeight: 600 }}>
                      <Typography color={styles.text.primary}>{order.order_number}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.primary}>{order.order_date}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.primary}>{item.product?.product_name || 'Unknown Product'}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.secondary}>
                        {item.color_name && item.size ? `${item.color_name} / ${item.size}` :
                         item.color_name || item.size || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.primary}>{item.quantity}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {statusLabel(order.status, t, item.cancel_exchange_request)}
                        {!item.cancel_exchange_request && (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={order.status}
                              onChange={(e) => handleUpdateStatus(order.id, e.target.value as Order['status'])}
                              variant="outlined"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                '& .MuiSelect-select': {
                                  padding: '2px 8px',
                                  backgroundColor: styles.paper.bgcolor,
                                  color: styles.text.primary
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: styles.table.cell.borderColor
                                }
                              }}
                            >
                              {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewOrder(order)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            color: styles.text.primary,
                            '&:hover': {
                              bgcolor: isDarkMode ? '#404040' : '#f5f5f5'
                            }
                          }}
                        >
                          {t('sellerOrders.viewDetail')}
                        </Button>
                        {(() => {
                          // 교환 승인된 경우는 정상 아이템으로 처리
                          const isExchangeApproved = item.cancel_exchange_request?.type === 'exchange' &&
                                                    item.cancel_exchange_request?.status === 'approved';
                          const hasActiveRequest = item.cancel_exchange_request && !isExchangeApproved;

                          if (hasActiveRequest && item.cancel_exchange_request) {
                            return (
                              <Button
                                size="small"
                                disabled
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  color: styles.text.secondary,
                                  cursor: 'not-allowed'
                                }}
                              >
                                {item.cancel_exchange_request.type === 'cancel' ? t('sellerOrders.cancelApplied') :
                                 item.cancel_exchange_request.type === 'exchange' ? t('sellerOrders.exchangeApplied') : t('sellerOrders.refundApplied')}
                              </Button>
                            );
                          }

                          if (order.status === 'preparing') {
                            return (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircle />}
                                onClick={() => handleUpdateStatus(order.id, 'confirmed')}
                                sx={{ ...styles.button.primary, textTransform: 'none', fontWeight: 500 }}
                              >
                                {t('sellerOrders.acceptOrder')}
                              </Button>
                            );
                          }

                          if (order.status === 'confirmed') {
                            return (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<LocalShippingOutlined />}
                                onClick={() => handleShipOrder(order)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  '&:hover': { bgcolor: '#1976d2' }
                                }}
                              >
                                {t('sellerOrders.startShipping')}
                              </Button>
                            );
                          }

                          return null;
                        })()}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )
            ) : (
              // 주문별 보기: 각 주문을 하나의 행으로 표시
              paginatedOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <TableRow>
                    <TableCell sx={{ ...styles.table.cell, fontWeight: 600 }}>
                      <Typography color={styles.text.primary}>{order.order_number}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.primary}>{order.order_date}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.primary}>{order.customer_name}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.primary}>{order.order_items?.length || 0}{t('sellerOrders.itemUnit')}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Typography color={styles.text.primary}>{(order.total_amount).toLocaleString()}{t('sellerOrders.currencyUnit')}</Typography>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {/* 주문에 포함된 아이템 중 취소/교환/환불 요청이 있는지 확인 */}
                        {(() => {
                          const hasCancelExchangeRequest = order.order_items?.some(item => item.cancel_exchange_request);
                          const cancelExchangeRequest = order.order_items?.find(item => item.cancel_exchange_request)?.cancel_exchange_request;

                          return statusLabel(order.status, t, hasCancelExchangeRequest ? cancelExchangeRequest : undefined);
                        })()}
                        {(() => {
                          // 교환 승인된 아이템은 제외하고 체크 (재발송을 위해 액션 필요)
                          const hasActiveRequest = order.order_items?.some(item =>
                            item.cancel_exchange_request &&
                            !(item.cancel_exchange_request.type === 'exchange' && item.cancel_exchange_request.status === 'approved')
                          );

                          return !hasActiveRequest && (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={order.status}
                                onChange={(e) => handleUpdateStatus(order.id, e.target.value as Order['status'])}
                                variant="outlined"
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  '& .MuiSelect-select': {
                                    padding: '2px 8px',
                                    backgroundColor: styles.paper.bgcolor,
                                    color: styles.text.primary
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: styles.table.cell.borderColor
                                  }
                                }}
                              >
                                {statusOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        })()}
                      </Stack>
                    </TableCell>
                    <TableCell sx={styles.table.cell}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleViewOrder(order)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            color: styles.text.primary,
                            '&:hover': {
                              bgcolor: isDarkMode ? '#404040' : '#f5f5f5'
                            }
                          }}
                        >
                          {t('sellerOrders.viewDetail')}
                        </Button>
                        {(() => {
                          // 교환 승인된 아이템은 제외하고 체크
                          const hasActiveRequest = order.order_items?.some(item =>
                            item.cancel_exchange_request &&
                            !(item.cancel_exchange_request.type === 'exchange' && item.cancel_exchange_request.status === 'approved')
                          );

                          if (hasActiveRequest) {
                            return (
                              <Button
                                size="small"
                                disabled
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  color: styles.text.secondary,
                                  cursor: 'not-allowed'
                                }}
                              >
                                {t('sellerOrders.cancelExchangeRefundApplied')}
                              </Button>
                            );
                          }

                          if (order.status === 'preparing') {
                            return (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircle />}
                                onClick={() => handleUpdateStatus(order.id, 'confirmed')}
                                sx={{ ...styles.button.primary, textTransform: 'none', fontWeight: 500 }}
                              >
                                {t('sellerOrders.acceptOrder')}
                              </Button>
                            );
                          }

                          if (order.status === 'confirmed') {
                            return (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<LocalShippingOutlined />}
                                onClick={() => handleShipOrder(order)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  '&:hover': { bgcolor: '#1976d2' }
                                }}
                              >
                                {t('sellerOrders.startShipping')}
                              </Button>
                            );
                          }

                          return null;
                        })()}
                        {/* 확장/축소 버튼 */}
                        <IconButton
                          size="small"
                          onClick={() => toggleOrderExpansion(order.id)}
                          sx={{ ml: 1, color: styles.text.primary }}
                        >
                          {expandedOrders.has(order.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {/* 확장된 상품 정보 */}
                  {expandedOrders.has(order.id) && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ ...styles.table.cell, p: 0 }}>
                        <Box sx={{ bgcolor: isDarkMode ? '#404040' : '#f5f5f5', p: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: styles.text.primary }}>
                            {t('sellerOrders.orderItems')}
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={styles.table.header}>
                                <TableCell sx={styles.table.cell}>{t('sellerOrders.productName')}</TableCell>
                                <TableCell sx={styles.table.cell}>{t('sellerOrders.option')}</TableCell>
                                <TableCell sx={styles.table.cell}>{t('sellerOrders.count')}</TableCell>
                                <TableCell sx={styles.table.cell}>{t('common.price')}</TableCell>
                                <TableCell sx={styles.table.cell}>{t('sellerOrders.subtotal')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {order.order_items?.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell sx={styles.table.cell}>
                                    <Box>
                                      <Typography color={styles.text.primary}>{item.product?.product_name || item.product_name}</Typography>
                                      {item.cancel_exchange_request && (
                                        <Box sx={{ mt: 1 }}>
                                          {statusLabel('', t, item.cancel_exchange_request)}
                                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: styles.text.secondary }}>
                                            {t('sellerOrders.reason')}: {item.cancel_exchange_request.reason}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={styles.table.cell}>
                                    <Typography color={styles.text.secondary}>
                                      {item.color_name && item.size ? `${item.color_name} / ${item.size}` :
                                       item.color_name || item.size || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={styles.table.cell}>
                                    <Typography color={styles.text.primary}>{item.quantity}</Typography>
                                  </TableCell>
                                  <TableCell sx={styles.table.cell}>
                                    <Typography color={styles.text.primary}>{(item.product_price).toLocaleString()}{t('sellerOrders.currencyUnit')}</Typography>
                                  </TableCell>
                                  <TableCell sx={styles.table.cell}>
                                    <Typography color={styles.text.primary}>{(item.total_price).toLocaleString()}{t('sellerOrders.currencyUnit')}</Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      <Paper sx={{ ...styles.paper, mt: 0 }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('sellerOrders.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('sellerOrders.displayedRows', { from, to, count })}
          sx={{
            color: styles.text.primary,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: styles.text.primary,
            },
            '& .MuiTablePagination-select': {
              color: styles.text.primary,
            },
            '& .MuiTablePagination-actions': {
              color: styles.text.primary,
            },
            '& .MuiIconButton-root': {
              color: styles.text.primary,
            },
          }}
        />
      </Paper>

      {/* 주문 상세 다이얼로그 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: styles.paper }}>
        <DialogTitle sx={{ color: styles.text.primary }}>
          {t('sellerOrders.orderInfo')} - {selectedOrder?.order_number}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* 주문 정보 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={styles.text.primary}>
                  {t('sellerOrders.orderInfo')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.orderNumber')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{selectedOrder.order_number}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.orderDate')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{selectedOrder.order_date}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.paymentMethod')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{selectedOrder.payment_method}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.paymentStatus')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{selectedOrder.payment_status}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* 구매자 정보 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={styles.text.primary}>
                  {t('sellerOrders.buyerInfo')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('common.name')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{selectedOrder.customer_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.contact')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{formatPhoneNumber(selectedOrder.customer_phone)}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* 배송지 정보 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={styles.text.primary}>
                  {t('sellerOrders.shippingInfo')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('orders.recipient')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{selectedOrder.recipient_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.recipientContact')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{formatPhoneNumber(selectedOrder.recipient_phone)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('common.address')}</Typography>
                    <Typography variant="body1" color={styles.text.primary} sx={{ whiteSpace: 'pre-line' }}>{selectedOrder.shipping_address}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.specialRequests')}</Typography>
                    <Typography variant="body1" color={styles.text.primary}>{selectedOrder.special_requests || t('sellerOrders.none')}</Typography>
                  </Grid>
                  {selectedOrder.order_items && selectedOrder.order_items.length > 0 && selectedOrder.order_items[0].shipping_company && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.courierCompany')}</Typography>
                        <Typography variant="body1" color={styles.text.primary}>{selectedOrder.order_items[0].shipping_company}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color={styles.text.secondary}>{t('sellerOrders.trackingNumberLabel')}</Typography>
                        <Typography variant="body1" color={styles.text.primary}>{selectedOrder.order_items[0].tracking_number || t('sellerOrders.notRegistered')}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>

              {/* 주문 상품 */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color={styles.text.primary}>
                  {t('sellerOrders.orderedProducts')}
                </Typography>
                <TableContainer component={Paper} sx={styles.paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={styles.table.header}>
                        <TableCell sx={styles.table.cell}>{t('sellerOrders.productName')}</TableCell>
                        <TableCell sx={styles.table.cell}>{t('sellerOrders.option')}</TableCell>
                        <TableCell sx={styles.table.cell}>{t('sellerOrders.count')}</TableCell>
                        <TableCell sx={styles.table.cell}>{t('common.price')}</TableCell>
                        <TableCell sx={styles.table.cell}>{t('sellerOrders.subtotal')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.order_items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={styles.table.cell}>
                            <Box>
                              <Typography color={styles.text.primary}>{item.product?.product_name || 'Unknown Product'}</Typography>
                              {item.cancel_exchange_request && (
                                <Box sx={{ mt: 1 }}>
                                  {statusLabel('', t, item.cancel_exchange_request)}
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: styles.text.secondary }}>
                                    {t('sellerOrders.reason')}: {item.cancel_exchange_request.reason}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.table.cell}>
                            <Typography color={styles.text.secondary}>
                              {item.color_name && item.size ? `${item.color_name} / ${item.size}` :
                               item.color_name || item.size || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={styles.table.cell}>
                            <Typography color={styles.text.primary}>{item.quantity}</Typography>
                          </TableCell>
                          <TableCell sx={styles.table.cell}>
                            <Typography color={styles.text.primary}>{(item.product_price).toLocaleString()}{t('sellerOrders.currencyUnit')}</Typography>
                          </TableCell>
                          <TableCell sx={styles.table.cell}>
                            <Typography color={styles.text.primary}>{(item.total_price).toLocaleString()}{t('sellerOrders.currencyUnit')}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="h6" color={styles.text.primary}>
                    {t('sellerOrders.totalPayment')}: {(selectedOrder.total_amount).toLocaleString()}{t('sellerOrders.currencyUnit')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: styles.text.secondary }}>{t('common.close')}</Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => selectedOrder && handlePrintOrder(selectedOrder)}
            sx={{
              borderColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#fff' : '#000',
              '&:hover': {
                borderColor: isDarkMode ? '#ccc' : '#333',
                color: isDarkMode ? '#ccc' : '#333',
                bgcolor: 'transparent'
              }
            }}
          >
            {t('sellerOrders.print')}
          </Button>
          {selectedOrder && !selectedOrder.order_items?.some(item => item.cancel_exchange_request) && selectedOrder.status === 'preparing' && (
            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={() => {
                handleUpdateStatus(selectedOrder.id, 'confirmed');
                setOpenDialog(false);
              }}
              sx={styles.button.primary}
            >
              {t('sellerOrders.acceptOrderBtn')}
            </Button>
          )}
          {selectedOrder && !selectedOrder.order_items?.some(item => item.cancel_exchange_request) && selectedOrder.status === 'confirmed' && (
            <Button
              variant="contained"
              color="info"
              startIcon={<LocalShippingOutlined />}
              onClick={() => {
                handleShipOrder(selectedOrder);
                setOpenDialog(false);
              }}
            >
              {t('sellerOrders.startShipping')}
            </Button>
          )}
          {selectedOrder && selectedOrder.order_items?.some(item => item.cancel_exchange_request) && (
            <Button
              disabled
              sx={{
                color: styles.text.secondary,
                cursor: 'not-allowed'
              }}
            >
              {t('sellerOrders.cannotProcessDue')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 배송 다이얼로그 */}
      <Dialog
        open={showShippingDialog}
        onClose={() => setShowShippingDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('sellerOrders.shippingInputTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* 택배사 선택 */}
            <FormControl fullWidth margin="normal">
              <Typography variant="body2" gutterBottom>{t('sellerOrders.courierSelect')}</Typography>
              <Select
                value={shippingCompany}
                onChange={(e) => setShippingCompany(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">{t('sellerOrders.selectCourier')}</MenuItem>
                {shippingCompanies.map((company) => (
                  <MenuItem key={company} value={company}>
                    {company}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 직접입력 선택 시 택배사명 입력 */}
            {shippingCompany === t('sellerOrders.manualInput') && (
              <TextField
                fullWidth
                margin="normal"
                label={t('sellerOrders.courierNameLabel')}
                value={customShippingCompany}
                onChange={(e) => setCustomShippingCompany(e.target.value)}
                placeholder={t('sellerOrders.courierNamePlaceholder')}
              />
            )}

            {/* 송장번호 입력 */}
            <TextField
              fullWidth
              margin="normal"
              label={t('sellerOrders.trackingInput')}
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder={t('sellerOrders.trackingInputPlaceholder')}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShippingDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmShipping}
            variant="contained"
            disabled={!shippingCompany || !trackingNumber.trim() || (shippingCompany === t('sellerOrders.manualInput') && !customShippingCompany.trim())}
          >
            {t('sellerOrders.startShipping')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
